import { Toast } from 'bootstrap';

// undo_toast.js — Gmail-style "Applicant rejected. [Undo]" toast.
//
// Two entry points:
//   1. On page load, look for a flash-rendered element carrying a
//      `data-undo-url` attribute (server sets it via flash[:undo_url]).
//      Auto-shows the toast and starts the 5-second dismiss timer.
//   2. Exposes window.showUndoToast(undoUrl, message) for AJAX flows that
//      want to trigger the toast without a page reload.
//
// The toast reuses Bootstrap 5's Toast component and mounts a container
// once per page. Undo posts to the given url with the CSRF token; on
// success we redirect to the applicants index so the row visibly returns.

const CSRF_META = 'meta[name="csrf-token"]';
const CONTAINER_ID = 'undo-toast-container';

function ensureContainer() {
  let el = document.getElementById(CONTAINER_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = CONTAINER_ID;
    el.className = 'toast-container position-fixed bottom-0 end-0 p-3';
    el.style.zIndex = 1100;
    document.body.appendChild(el);
  }
  return el;
}

function csrfToken() {
  const meta = document.querySelector(CSRF_META);
  return meta ? meta.getAttribute('content') : '';
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function buildToast(undoUrl, message, undoLabel) {
  const wrapper = document.createElement('div');
  wrapper.className = 'toast align-items-center text-bg-dark border-0';
  wrapper.setAttribute('role', 'status');
  wrapper.setAttribute('aria-live', 'polite');
  wrapper.setAttribute('aria-atomic', 'true');
  wrapper.innerHTML = `
    <div class="d-flex">
      <div class="toast-body">${escapeHtml(message)}</div>
      <button type="button" class="btn btn-link text-info me-2" data-undo-btn>${escapeHtml(undoLabel)}</button>
      <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>`;
  wrapper.querySelector('[data-undo-btn]').addEventListener('click', () => {
    const fd = new FormData();
    fd.append('authenticity_token', csrfToken());
    fetch(undoUrl, {
      method: 'POST',
      body: fd,
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(r => r.json())
      .then(json => {
        // Reload the current view either way — on success the row is back;
        // on 'expired' the alert flash tells the user why.
        if (json.ok) window.location.reload();
        else window.location.reload();
      })
      .catch(() => { window.location.reload(); });
  });
  return wrapper;
}

function showUndoToast(undoUrl, message, undoLabel) {
  const container = ensureContainer();
  const toastEl = buildToast(undoUrl, message || 'Applicant rejected.', undoLabel || 'Undo');
  container.appendChild(toastEl);
  // Bootstrap 5 Toast — 5s auto-dismiss.
  const toast = new Toast(toastEl, { autohide: true, delay: 5000 });
  toast.show();
  toastEl.addEventListener('hidden.bs.toast', () => toastEl.remove());
}

// Server-flash entry point: look for #undo-toast-payload emitted from the
// layout when flash[:undo_url] is set.
document.addEventListener('DOMContentLoaded', () => {
  const payload = document.getElementById('undo-toast-payload');
  if (payload && payload.dataset.undoUrl) {
    showUndoToast(
      payload.dataset.undoUrl,
      payload.dataset.message,
      payload.dataset.undoLabel
    );
    payload.remove();
  }
});

window.showUndoToast = showUndoToast;
